<script setup lang="ts">
import PredictionResult from "@/views/Calculators/TarotPrediction/DailyPrediction/components/PredictionResult.vue";
import { ref } from "vue";
import { TarotDailyPredictionResponse } from "@/types/calculators";
import { ROUTE_NAMES } from "@/constants/routeNames";
import { fetchTarotDailyPrediction } from "@/api/calculators";
import { useSafeBack } from "@/utils/useSafeBack";
import BackButton from "@/components/buttons/BackButton.vue";
import GradientButton from "@/components/buttons/GradientButton.vue";
import { useI18n } from "vue-i18n";
import PredictionInstruction from "@/views/Calculators/TarotPrediction/DailyPrediction/components/PredictionInstruction.vue";

const { t } = useI18n();
const { safeBack } = useSafeBack();

const loading = ref<boolean>(false);
const predictionResult = ref<TarotDailyPredictionResponse | null>(null);

async function getPrediction(): Promise<void> {
  loading.value = true;
  try {
    predictionResult.value = await fetchTarotDailyPrediction();
  } finally {
    loading.value = false;
  }
}

function handleBack() {
  if (predictionResult.value) {
    predictionResult.value = null;
    return;
  }
  safeBack(ROUTE_NAMES.TAROT);
}
</script>

<template>
  <div
    class="flex flex-col gap-6 h-full xl:h-screen w-full pt-6 xl:pt-[88px] pb-6 px-4"
  >
    <div class="relative items-center md:hidden">
      <div class="absolute left-0">
        <BackButton @clicked="handleBack" />
      </div>
      <h2 class="text-2xl font-medium leading-[32px] text-brown">
        {{ t("calculators.tarot.daily-card.title") }}
      </h2>
    </div>
    <div
      class="text-left md:mx-auto py-6 px-4 md:px-0 bg-white rounded-[20px] flex flex-col items-center w-full md:max-w-[812px] h-full min-h-0 overflow-y-auto no-scrollbar"
    >
      <div
        class="relative hidden md:flex justify-center items-center w-full min-h-11 mb-2 px-10"
      >
        <div class="absolute left-10">
          <BackButton @clicked="handleBack" />
        </div>
        <h2 class="text-2xl font-semibold leading-[32px] text-brown">
          {{ t("calculators.tarot.daily-card.title") }}
        </h2>
      </div>
      <p
        class="text-sm font-normal leading-[20px] text-muted italic mb-10 md:px-10"
        :class="{ 'hidden md:block': predictionResult }"
      >
        {{ t("calculators.tarot.daily-card.short-tip") }}
      </p>
      <GradientButton
        v-if="!predictionResult"
        class="w-[225px] min-h-14 mb-10"
        bg-color="bg-brown-gradient"
        hover-bg-color="bg-orange-gradient"
        :text="t('calculators.tarot.daily-card.get-card-btn')"
        @click="getPrediction"
        :is-loading="loading"
      />
      <PredictionInstruction v-if="!predictionResult" />
      <PredictionResult v-else :result="predictionResult" />
    </div>
  </div>
</template>
